/**
 * Redaction boundary for everything shipped to Datadog (Logs and RUM).
 *
 * Password-reset and vendor-onboarding flows carry `signature`, `expires` and
 * `token` in the query string. That is auth material, not PII — anyone holding
 * Datadog log access holds a replayable link. Several sinks ship those URLs
 * with no application code involved at all (the Logs SDK forwards every failed
 * request's URL, and stamps `view.url` onto every log line from anywhere in the
 * app), so redacting at the `logger.*` call sites cannot close the leak. This
 * module is applied inside both SDK `beforeSend` hooks, which is the only seam
 * that sees those events.
 *
 * Pure string functions with no imports, so it can be statically imported from
 * `datadog-init.tsx` without pulling the dynamically-imported SDK chunk into the
 * main bundle (`src/helper/correlation-id.ts` is the precedent for that shape).
 */

const REDACTED = 'REDACTED';

/** Stand-in for a network error's captured response body. */
export const REDACTED_RESPONSE_BODY = '[redacted: network error response body]';

/**
 * Allowlist, not denylist, and values are redacted while keys are preserved.
 *
 * A denylist fails the moment the backend adds a new signed param, and sink
 * inventories in this repo have already been shown to drift — the maintenance
 * mode sink was added after this leak was first reported. Keeping the key and
 * redacting only the value means request shape stays diagnosable, so nobody has
 * to widen the allowlist just to discover which params a URL carried.
 *
 * Operational params only. Free-text search is deliberately absent: a search
 * box on the work queue takes resident names and addresses. Auth-shaped params
 * (`state`, `code`, `token`, `nonce`) are absent for the same reason even
 * though some of them are sometimes benign — the cost of a wrong guess is
 * asymmetric.
 */
const SAFE_QUERY_PARAMS = new Set([
  // Pagination
  'page',
  'per_page',
  'perPage',
  'limit',
  'offset',
  'cursor',
  // Sorting
  'sort',
  'sort_by',
  'sortBy',
  'order',
  'order_by',
  'orderBy',
  'direction',
  // View state
  'tab',
  'view',
  'status',
  'filter',
  'category',
  'subcategory',
  'mode',
  'step',
  'priority',
  // Date ranges
  'date',
  'start_date',
  'startDate',
  'end_date',
  'endDate',
  'range',
  'timezone',
  'tz',
  // Opaque resource ids — high diagnostic value, not personal data
  'id',
  'woId',
  'work_order_id',
  'workOrderId',
  'serviceRequestId',
  'service_request_id',
  'quoteId',
  'quote_id',
  'propertyId',
  'property_id',
  'vendorId',
  'vendor_id',
  'marketId',
  'market_id',
  'organizationId',
  'organization_id',
  // Next.js RSC payload marker — carries no value, but its presence explains
  // why a navigation issued a fetch at all.
  '_rsc',
]);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_ID_RE = /^\d+$/;
const OPAQUE_TOKEN_RE = /^[A-Za-z0-9._~-]{16,}$/;
const SCHEME_HOST_RE = /^([a-z][a-z0-9+.-]*:\/\/[^/]+)(\/.*)?$/i;
const ABSOLUTE_URL_IN_TEXT_RE = /https?:\/\/[^\s"'<>]+/g;
/** Splits a matched URL from punctuation that belonged to the surrounding prose. */
const TRAILING_PUNCTUATION_RE = /^(.*?)([)\]}>.,;:!?'`]*)$/;
/** `scheme://user:pass@host` — credentials the browser accepts and we must never ship. */
const USERINFO_RE = /^([a-z][a-z0-9+.-]*:\/\/)[^@/]*@/i;

/**
 * Replaces high-cardinality path segments with placeholders, so a route stays
 * groupable and a signed token in a path segment never reaches the backend.
 *
 * Mirrors the equivalent helper in lula-lumi-fe. The letters-and-digits test on
 * opaque segments is what keeps real route names like `service-requests` and
 * `verify-otp` intact.
 */
function normalizePath(path: string): string {
  return path
    .split('/')
    .map((segment) => {
      if (!segment) return segment;
      if (UUID_RE.test(segment)) return ':id';
      if (NUMERIC_ID_RE.test(segment)) return ':id';
      if (OPAQUE_TOKEN_RE.test(segment) && /[A-Za-z]/.test(segment) && /\d/.test(segment)) {
        return ':token';
      }
      return segment;
    })
    .join('/');
}

/**
 * Redacts the value of every query param outside the allowlist, preserving key
 * order and duplicates.
 */
function redactQuery(query: string): string {
  if (!query) return '';

  // URLSearchParams never throws, which matters here: a beforeSend that throws
  // takes down telemetry silently.
  const params = new URLSearchParams(query);
  const parts: string[] = [];

  params.forEach((value, key) => {
    if (SAFE_QUERY_PARAMS.has(key)) {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    } else {
      parts.push(`${encodeURIComponent(key)}=${REDACTED}`);
    }
  });

  return parts.join('&');
}

/**
 * Redacts unsafe query values from a URL and, when asked, collapses the path to
 * a route template.
 *
 * `normalizeRoute` is for `view.url` / `view.referrer`, where cardinality is the
 * point. It is deliberately NOT applied to `http.url` on network errors — the
 * real path is what makes a failed request diagnosable, and the credential
 * lives in the query string, which is redacted either way.
 *
 * The fragment is dropped rather than redacted: it is never sent to a server,
 * so it has no diagnostic value here, and OTP flows have been known to park
 * tokens in it.
 *
 * Never throws, and is idempotent — scrubbing an already-scrubbed URL is a
 * no-op, which matters because RUM view events are re-sent as they update.
 */
export function scrubUrl(
  rawUrl: string | undefined | null,
  { normalizeRoute = false }: { normalizeRoute?: boolean } = {},
): string {
  if (!rawUrl || typeof rawUrl !== 'string') return '';

  const [withoutHash] = rawUrl.split('#');
  const queryStart = withoutHash.indexOf('?');
  const beforeQuery = queryStart === -1 ? withoutHash : withoutHash.slice(0, queryStart);
  const query = queryStart === -1 ? '' : withoutHash.slice(queryStart + 1);

  let origin = '';
  let path = beforeQuery;

  const schemeHostMatch = beforeQuery.match(SCHEME_HOST_RE);
  if (schemeHostMatch) {
    // `[^/]+` in SCHEME_HOST_RE swallows any `user:pass@` into the origin, which
    // would then be re-emitted verbatim. Nothing in this app builds such a URL
    // today, but this module bills itself as the redaction boundary for
    // everything shipped to Datadog — a boundary that is only correct for the
    // URL shapes that happen to exist right now is one that degrades silently.
    origin = (schemeHostMatch[1] ?? '').replace(USERINFO_RE, '$1');
    path = schemeHostMatch[2] ?? '';
  }

  // The origin is kept: this app serves two of them (the coordinator host and
  // the vendor portal), and which one a session was on is diagnostic.
  const scrubbedPath = normalizeRoute ? normalizePath(path) : path;
  const scrubbedQuery = redactQuery(query);

  return `${origin}${scrubbedPath}${scrubbedQuery ? `?${scrubbedQuery}` : ''}`;
}

/**
 * Redacts every absolute URL embedded in a free-text string.
 *
 * The Logs SDK builds network-error messages by interpolation —
 * `XHR error GET https://host/path?signature=…` — so the credential arrives in
 * a message field rather than a URL field, where no per-field scrub reaches it.
 *
 * Only `http(s)` URLs are matched, so the `chrome-extension://` scheme the
 * existing extension filter keys on is left untouched.
 */
export function scrubUrlsInText(text: string | undefined | null): string {
  if (!text || typeof text !== 'string') return '';
  return text.replace(ABSOLUTE_URL_IN_TEXT_RE, (match) => {
    // The character class stops only at whitespace and quotes, so a delimiter
    // that follows the URL in prose — `)` in a stack frame, `,` in a list, `:`
    // before a status — is swallowed into the match, lands in the last query
    // value, and is then destroyed when the query is rebuilt. Hand it back.
    //
    // It fails closed either way (the credential is redacted), but it mangles
    // exactly the strings this module exists to keep readable: a stack frame is
    // only rewritten when it CARRIES a credential, so the redacted frames would
    // be the unparseable ones.
    const [, url, trailing] = match.match(TRAILING_PUNCTUATION_RE) ?? [null, match, ''];
    return `${scrubUrl(url)}${trailing}`;
  });
}
